import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ClearIcon from '@material-ui/icons/Clear';
import { Box, Button, Typography, Chip, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

import MTSStyle from './MTSStyle';
import { build } from 'helper/build';
import { _isEqual, deepClone } from 'helper/helper';
import { isCalFamily } from '../filter/FilterCondition';
import { getMutlipleDateRange } from 'helper/dateRange';
import { calculateTotalColumnCount, hasPivotedColumn } from '../Preview.utils';
import {
	setFilterList,
	setSelectedList,
	resetPivotOptions,
	setIsMTSFilterCollapsed
} from '../Preview.actions';
import {
	CLEAR_ALL,
	CANCEL,
	APPLY,
	MULTI_TIME_SELECTION,
	PIVOT_COLUMN_LIMIT
} from 'constants/constant';

const dateFormat = 'MM/DD/YYYY';

/**
 * MultiTimeSelectionFilter
 * This Input Field will open in filter Panel when user clicks on Multi-time Filter Icon
 * When user clicks on this Input, it will open the MultiTimeSelectionWidget
 */
const MultiTimeSelectionFilter = ({
	mtsState,
	selectedView,
	onInputClick,
	goBack,
	parentPanel,
	templateId,
	onMTSUpdate,
	userPersona,
	updateCurrentAndPrevFilterList
}) => {
	const classes = MTSStyle();
	const dispatch = useDispatch();
	const [newChipValues, setNewChipValues] = useState([]);

	const { selectedList, pivotOpts } = useSelector((state) => state.previewReducer);
	const templateData = useSelector((state) => state.templateReducer.templateData);
	const prevFilterState = useSelector((state) => state.previewReducer.filterList);
	const isMtsFilterCollapsed = useSelector((state) => state.previewReducer.isMtsFilterCollapsed);

	useEffect(() => {
		const chipValuesArray = Object.entries(mtsState ?? {}).map((entry) => {
			const key = entry[0];
			const value = entry[1];
			return { key, value };
		});
		setNewChipValues(chipValuesArray);
	}, [mtsState]);

	const onChipDelete = (newItems) => setNewChipValues(newItems);

	const addDateStrings = (mtsFilterState) => {
		if (mtsFilterState?.CUSTOM_DATE_1?.date?.start) {
			mtsFilterState.CUSTOM_DATE_1.date.startDateString =
				mtsFilterState?.CUSTOM_DATE_1?.date?.start.toString();
			mtsFilterState.CUSTOM_DATE_1.date.endDateString =
				mtsFilterState?.CUSTOM_DATE_1?.date?.end.toString();
		}
		if (mtsFilterState?.CUSTOM_DATE_2?.date?.start) {
			mtsFilterState.CUSTOM_DATE_2.date.startDateString =
				mtsFilterState?.CUSTOM_DATE_2?.date?.start.toString();
			mtsFilterState.CUSTOM_DATE_2.date.endDateString =
				mtsFilterState?.CUSTOM_DATE_2?.date?.end.toString();
		}
		return mtsFilterState;
	};

	const onSubmit = () => {
		const updatedChipValues = newChipValues.reduce((accumulator, currentValue) => {
			return { ...accumulator, [currentValue.key]: currentValue.value };
		}, {});
		let _subAtt = {};

		const newList = deepClone(prevFilterState);
		const index = newList.findIndex((el) => {
			return el?.localName === MULTI_TIME_SELECTION;
		});

		/**
		 * | Below "if" condition to edit old reports where filtertable and filterattr
		 * | will not come in runQueryRequestObject. Hence, need to take it from template data.
		 * |
		 */
		if (isCalFamily(newList[index])) {
			const parent = newList[index].parent;
			const _template = templateData.find((t) => Number(t.templateId) === templateId);
			const _o = _template?.time.find((_t) => _t.businessName === parent);
			_subAtt = _o?.subAttributes.find((s) => s.businessName === 'Week');
		}
		const value = Object.keys(updatedChipValues ?? {})
			?.filter((key) => !['CUSTOM_DATE_1', 'CUSTOM_DATE_2'].includes(key))
			?.map((key) => getMutlipleDateRange(key))
			?.filter((val) => val !== undefined);
		// Edit exiting filter when user clicks on "Filter" title.
		// 1. form the 'value' and 'extraValue' array by using getMutlipleDateRange function
		// 2. form 'condition' by joining all fuzzy options into one comma separated string
		newList[index] = {
			...newList[index],
			..._subAtt,
			mts: {
				...addDateStrings(updatedChipValues)
			},
			filter: {
				condition: Object.keys(updatedChipValues ?? {})
					?.filter((key) => !['CUSTOM_DATE_1', 'CUSTOM_DATE_2'].includes(key))
					.join(','),
				value: value,
				extraValue: value
			}
		};
		if (Object.keys(newList[index]?.mts ?? {}).length > 0) {
			const field = build(newList[index], selectedView, userPersona, true);
			newList[index] = { ...newList[index], field };
		} else {
			delete newList[index]?.field;
		}

		// Updates the prevFilterState and filterState which is local to FilterForm.component.js and not something from redux
		updateCurrentAndPrevFilterList(newList);
		// Updating redux filterState
		dispatch(setFilterList(newList));
		/**
		 * If Pivot is applied and user has selected the time filters,
		 * - Then check total column count and reset the Pivot, if limit exceeds.
		 * - Also count of filter applied is less than 2, Reset the Pivot.
		 */
		if (hasPivotedColumn(pivotOpts?.pivotColumn)) {
			const totalCount = calculateTotalColumnCount(selectedList, newList, pivotOpts.isTimeAcross);
			if (totalCount > PIVOT_COLUMN_LIMIT || newChipValues.length < 2) {
				dispatch(resetPivotOptions());
			}
		}
		if (!_isEqual(mtsState, updatedChipValues)) {
			onMTSUpdate(updatedChipValues);
		}
		if (parentPanel === 'filterPanel') {
			dispatch(setIsMTSFilterCollapsed(true));
		} else {
			goBack();
		}
	};

	const onDelete = () => {
		// Remove MTS column
		dispatch(
			setSelectedList(selectedList?.filter((item) => item?.localName !== MULTI_TIME_SELECTION))
		);
		// Deletes all MTS filters applied
		const filterState = deepClone(prevFilterState);
		filterState.forEach((filter) => {
			if (filter?.localName === MULTI_TIME_SELECTION) {
				filter.filter = {
					condition: '',
					value: [],
					extraValue: []
				};
				filter.field = undefined;
				delete filter?.localName;
				delete filter?.mts;
			}
		});

		// Updates the prevFilterState and filterState which is local to FilterForm.component.js and not something from redux
		updateCurrentAndPrevFilterList(filterState);
		// Updating redux filterState
		dispatch(setFilterList(filterState));
		onMTSUpdate({});
		// Reset Pivot if user has applied
		if (pivotOpts?.pivotColumn) {
			dispatch(resetPivotOptions());
		}
		if (parentPanel === 'filterPanel') {
			dispatch(setIsMTSFilterCollapsed(true));
		} else {
			goBack();
		}
	};

	const clearAll = () => {
		onMTSUpdate({});
	};

	/**
	 * When user is on Filter Panel, this Filter component is replaced with Title & current selections.
	 * On clicks of this component, it will open the Filter component with input chips
	 */
	if (isMtsFilterCollapsed) {
		let selectedFilterValues = 'Select Value';
		if (newChipValues.length > 0) {
			selectedFilterValues = newChipValues
				.map(({ key, value }) => {
					// Format Custom range selection
					if (key.includes('CUSTOM_DATE')) {
						if (value?.rangeType === 'day') {
							return `${moment(value?.date?.start).format(dateFormat)} - ${moment(
								value?.date?.end
							).format(dateFormat)}`;
						}
						if (value?.rangeType === 'week') {
							return `Week ${value?.date?.week1} - Week ${value?.date?.week2} (${moment(
								value?.date?.start
							).format(dateFormat)} - ${moment(value?.date?.end).format(dateFormat)})`;
						}
					}
					// No format needed for other types
					return value;
				})
				.join(', ');
		}
		return (
			<Box
				className={classes.MTSFilterContainerCollapsed}
				onClick={() => dispatch(setIsMTSFilterCollapsed(false))}>
				<Typography>Multi time selection</Typography>
				<Typography>{selectedFilterValues}</Typography>
			</Box>
		);
	}

	return (
		<Box className={classes.MTSFilterContainer}>
			<Box className={classes.MTSFilterTitleBox}>
				<Typography>Multi time selection</Typography>
				<DeleteOutlineIcon
					fontSize="small"
					onClick={onDelete}
					id="delete"
					className={classes.MTSFilterDeleteIcon}
				/>
			</Box>
			{/* Input */}
			<Box onClick={onInputClick}>
				<InputFieldWithChip chipValues={newChipValues} onChipDelete={onChipDelete} />
			</Box>
			{/* Action buttons */}
			<Box display={'flex'}>
				<Button
					id="multi-time-selection-filter-clear-all"
					disableRipple
					className={classes.clearAllBtn}
					onClick={clearAll}>
					{CLEAR_ALL}
				</Button>
				<Box className={classes.MTSActionRight}>
					<Button
						id="multi-time-selection-filter-cancel"
						disableRipple
						onClick={goBack}
						className={`${classes.MTSActionBtn} ${classes.MTSSecondaryBtn}`}>
						{CANCEL}
					</Button>
					<Button
						id="multi-time-selection-filter-apply"
						disableRipple
						onClick={onSubmit}
						className={`${classes.MTSActionBtn} ${classes.MTSPrimaryBtn}`}>
						{APPLY}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};

export const InputFieldWithChip = ({ chipValues, onChipDelete }) => {
	const classes = MTSStyle();
	return (
		<Autocomplete
			freeSolo
			multiple
			options={[]}
			clearIcon={false}
			value={chipValues}
			onClick={onChipDelete}
			className={classes.MTSFilterInput}
			onChange={(e, newItems) => onChipDelete(newItems)}
			renderTags={(value, props) =>
				value.map((option, index) => {
					let label = option.value;
					if (option.value?.rangeType === 'day') {
						label = `${moment(option.value?.date?.start).format(dateFormat)} - ${moment(
							option.value?.date?.end
						).format(dateFormat)}`;
					}
					if (option.value?.rangeType === 'week') {
						label = `Week ${option.value?.date?.week1} - Week ${
							option.value?.date?.week2
						} (${moment(option.value?.date?.start).format(dateFormat)} - ${moment(
							option.value?.date?.end
						).format(dateFormat)})`;
					}

					return (
						<Chip
							clickable
							key={index}
							label={label}
							deleteIcon={<ClearIcon />}
							{...props({ index })}
						/>
					);
				})
			}
			renderInput={(params) => <TextField variant="outlined" label="Time range" {...params} />}
		/>
	);
};

export default MultiTimeSelectionFilter;
